<script lang="ts" setup>
import dayjs from 'dayjs'
import { PRODUCT_CODE } from '../constants'
import { commaizeNumber } from '@/utils/primitive/number'
import { GoodsDetailResponse } from '~/api/types/goods'
import Benefit from './benefit-info.vue'
import HeadCopyCoupon from './head-copy-coupon.vue'
import { useTooltip, TOOLTIP_TYPE, useBenefit } from '~/pages/goods/composables/ui'
import { useUserStore } from '~/store/user'
import { useGoodsStore } from '~/store/store-goods'
import ModalInterestFree from '~/pages/goods/components/layer/modal-interest-free.vue'

type Key = (typeof TAB_TYPE)[keyof typeof TAB_TYPE]

type EmitInfo = {
  id: string
  type: string
}

export interface Props {
  items: GoodsDetailResponse
  isDesktop: boolean
}

const props = withDefaults(defineProps<Props>(), {})
const emit = defineEmits<(event: 'update:modelValue', result: EmitInfo) => void>()

const goodsInfo = toRef(props, 'items')
const goodsStore = useGoodsStore()
const userStore = useUserStore()
const { goods } = storeToRefs(goodsStore)
const { custInfo } = storeToRefs(userStore)

const { setTooltips, checkIsOpen, changeTooltips } = useTooltip()
setTooltips({ uuid: TOOLTIP_TYPE.INFO1, isOpen: false })
setTooltips({ uuid: TOOLTIP_TYPE.INFO2, isOpen: false })
const btnTooltipDiscount = computed(() => checkIsOpen(TOOLTIP_TYPE.INFO1))
const btnTooltipStaffDiscount = computed(() => checkIsOpen(TOOLTIP_TYPE.INFO2))

const { goodsCoupon, marketingCoupon } = useBenefit()

const isShowInterestFree = ref<boolean>(false)

const TAB_TYPE = { TAB1: 'TAB1', TAB2: 'TAB2', TAB3: 'TAB3', TAB4: 'TAB4' } as const
const today = dayjs().format('YYYYMMDD')
const checkToday = computed(() => {
  const current = goodsInfo.value?.promotionInfo?.acamtApplyEndDttm.replace(/\./g, '')
  if (today === current) {
    return true
  }
})

const emitInfo = ref<EmitInfo>({
  id: 'TAB1',
  type: ''
})
const headCopyText = computed(() => {
  if (goodsInfo.value?.headCopyInfo?.headCopyList) {
    const goodsTag = goodsInfo.value?.headCopyInfo?.headCopyList.filter(
      (v: { epsuSpr: string }) => v.epsuSpr === 'GDS_TAG'
    )
    if (goodsTag.length > 0) {
      return goodsTag[0].textCtnt.length > 20 ? goodsTag[0].textCtnt.slice(0, 20) + '...' : goodsTag[0].textCtnt
    }
  }
})

/** 원산지 */
const isGoodsOrigin = computed(() => {
  const clssfCd = goodsInfo.value?.detailInfo?.ftcGoodsClssfCd
  if (clssfCd) {
    return PRODUCT_CODE.CAT_GB.ORGIN.includes(clssfCd)
  }
})

/** 상담상품 여부 */
const isConsultantGoods = computed(() => {
  const isDispTypedCd = goodsInfo.value?.detailInfo?.dispTypeCd
  if (isDispTypedCd) {
    return PRODUCT_CODE.GOODS_DISPLAY_TYPE.CONSULTATION.includes(isDispTypedCd)
  } else {
    return false
  }
})

/** 렌탈상품 여부 */
const isRentalGoods = computed(() => {
  const isDispTypedCd = goodsInfo.value?.detailInfo?.dispTypeCd
  if (isDispTypedCd) {
    return PRODUCT_CODE.GOODS_DISPLAY_TYPE.FORMLESS_RENTAL.includes(isDispTypedCd)
  } else {
    return false
  }
})

/** 방송상품 여부 */
const isBroadCastGoods = computed(
  () => goodsInfo.value?.detailInfo?.busChnId === 'TV' || goodsInfo.value?.detailInfo?.busChnId === 'CTCOM'
)

/** 할인율 */
const setSaleRate = computed(() => {
  const dcRate = goodsInfo?.value?.detailInfo?.dcRate
  if (dcRate && dcRate < 1) {
    return
  } else {
    return dcRate
  }
})

/** 상품평 개수*/
const goodsReview = computed(() => Number(goodsInfo.value?.detailInfo?.ptcptCnt))
/** 주문수량 */
const orderQty = computed(() => Number(goodsInfo.value?.detailInfo?.ordQty))
/** 별점 */
const ratingPoint = computed(() => goodsInfo?.value?.detailInfo?.score.toFixed(1))

/** 적립금 */
const padValRto = computed(() => {
  if (goodsInfo.value?.promotionInfo?.acamtDcWay === 'AMT') {
    return checkToday.value ? `오늘까지` : `최대`
  } else {
    return `${checkToday.value ? '오늘까지' : '최대'} ${goodsInfo.value?.promotionInfo?.acamtRto}%`
  }
})

/** 할인유무 */
const isDc = computed(() => goodsInfo.value?.detailInfo?.salePrice === goodsInfo.value?.detailInfo?.dcPrice)
/** 판매가격 */
const setOfferPrice = computed(() => commaizeNumber(Number(goodsInfo.value?.detailInfo?.dcPrice)))
/** 기존가격 */
const setSalePrice = computed(() => commaizeNumber(Number(goodsInfo.value?.detailInfo?.salePrice)))
/** 임직원가격 */
const setStaffSalePrice = computed(() => commaizeNumber(Number(goodsInfo.value?.detailInfo?.staffDcPrice)))
/** 월 렌탈가격 */
const monthlyRentalPrice = computed(() => {
  if (!isBroadCastGoods.value && goodsInfo.value?.detailInfo?.mmRntalPrc > 0) {
    return commaizeNumber(goodsInfo.value?.detailInfo?.mmRntalPrc)
  }
})

/** 의무사용기간 */
const dutyUseDur = computed(() => {
  const dutyUseDur = goodsInfo.value?.detailInfo?.dutyUseDur
  const dispTypeCd = goodsInfo.value?.detailInfo?.dispTypeCd
  let dutyTitle = ''

  if (Number(dutyUseDur) > 0) {
    if (dispTypeCd !== '35' && dispTypeCd !== '40' && dispTypeCd !== '56' && dispTypeCd !== '58') {
      dutyTitle = '의무 사용 기간 '
      if (dispTypeCd === '57') {
        dutyTitle = '약정기간 '
      }
    }
  }

  return dutyUseDur ? `${dutyTitle + commaizeNumber(dutyUseDur)}개월` : null
})

/** 카드할인 */
const hasCardDiscount = computed(() => {
  const cardBnftList = goodsInfo.value?.promotionInfo?.cardBnftList
  if (cardBnftList && cardBnftList.length > 0) {
    return true
  }
})

/** 혜택 */
const hasBenefit = computed(() => {
  const promotionInfo = goodsInfo.value?.promotionInfo
  if (
    Number(promotionInfo?.lspAmt) > 0 ||
    Number(promotionInfo?.sbDcAmt) ||
    (promotionInfo?.cardBnftList && promotionInfo?.cardBnftList.length > 0) ||
    (promotionInfo?.cpBnftList && promotionInfo?.cpBnftList.length > 0)
  ) {
    return true
  }
})

/** 할인가 */
const dcPriceInfo = computed(() => {
  if (goodsInfo.value) {
    const coupon1 = goodsCoupon(goodsInfo.value)[1] ? Number(goodsCoupon(goodsInfo.value)[1].replaceAll(/,/g, '')) : 0
    const coupon2 = Number(goodsInfo.value?.promotionInfo?.lspAmt)
    const coupon3 = marketingCoupon(goodsInfo.value)[1]
      ? Number(marketingCoupon(goodsInfo.value)[1].replaceAll(/,/g, ''))
      : 0
    const coupon4 = Number(goodsInfo.value?.promotionInfo?.sbDcAmt)
    return coupon1 + coupon2 + coupon3 + coupon4
  }
})

/** 최대 무이자 할부 개월 */
const iefrInstmMnmnt = computed(() => {
  return goodsInfo.value?.promotionInfo?.iefrInstmMnmnt ?? 0
})

const cardFreeInstmList = computed(() => {
  return goodsInfo.value?.promotionInfo?.cardFreeInstmList ?? []
})

const freeInterestMonthByCard = computed(() => {
  const freeInstmList = cardFreeInstmList.value
    .map(card => {
      return {
        cardCoCd: card.cardCoCd,
        months: card.instmMnmnt
          .split(',')
          .filter(Boolean)
          .map(month => Number(month))
      }
    })
    .filter(card => card.months.length > 0)
  return freeInstmList
})

const maxInterestFreeNumber = computed(() => {
  let max = iefrInstmMnmnt.value
  freeInterestMonthByCard.value.forEach(card => {
    const month = card.months[card.months.length - 1]
    max = Math.max(month, max)
  })
  return max
})

/** 일시불 할인금액을 제외한 임직원할인가 */
const staffDiscountAfterLsp = computed(() => {
  const salePrice = Number(goodsInfo.value?.detailInfo?.salePrice) || 0
  const staffDcPrice = Number(goodsInfo.value?.detailInfo?.staffDcPrice) || 0
  const lspAmt = Number(goodsInfo.value?.promotionInfo?.lspAmt) || 0
  return commaizeNumber(salePrice - staffDcPrice - lspAmt)
})

/** 임직원 할인 총액 */
const staffDiscountAllPrice = computed(() => {
  const salePrice = Number(goodsInfo.value?.detailInfo?.salePrice) || 0
  const staffDcPrice = Number(goodsInfo.value?.detailInfo?.staffDcPrice) || 0
  return commaizeNumber(salePrice - staffDcPrice)
})

function _onChangeTab(id: Key, type: string) {
  emitInfo.value.id = id
  emitInfo.value.type = type
  emit('update:modelValue', emitInfo.value)
}
</script>
<template>
  <div class="detail-info-wrap">
    <span class="head-copy">{{ headCopyText }}</span>
    <h3 class="goods-name">
      {{ goodsInfo?.detailInfo?.productNm }}
    </h3>

    <button type="button" class="btn-origin" v-if="isGoodsOrigin" @click="_onChangeTab(TAB_TYPE.TAB2, 'remtrlpoo')">
      원산지: {{ isGoodsOrigin ? '상품정보 참조' : '' }}
    </button>
    <div class="price-wrap">
      <div v-if="!isConsultantGoods">
        <span class="dc-rate" v-if="setSaleRate"> {{ setSaleRate }}%</span>
        <span :class="[isDc ? 'current-price' : 'dc-price']"
          >{{ setOfferPrice }}<em>원{{ goodsInfo?.detailInfo?.prcWaveDisp }}</em>
        </span>
        <div class="tooltip-wrap" v-if="!hasCardDiscount && hasBenefit">
          <button type="button" class="btn-tooltip" @click="changeTooltips(TOOLTIP_TYPE.INFO1)">
            <span class="blind">안내</span>
          </button>
          <div class="tooltip-box" v-if="btnTooltipDiscount">
            <div class="benefit-info">
              <dl class="dc-price-info">
                <dt>총 가격 할인</dt>
                <dd>
                  <em>-{{ commaizeNumber(Number(dcPriceInfo)) }}</em
                  >원{{ goodsInfo?.detailInfo?.prcWaveDisp }}
                </dd>
              </dl>

              <div class="benefit-list">
                <dl v-if="goodsCoupon(goodsInfo).length > 0">
                  <dt>상품 {{ goodsCoupon(goodsInfo)[0] }} 할인</dt>
                  <dd>
                    <em>-{{ goodsCoupon(goodsInfo)[1] }}</em
                    >원{{ goodsInfo?.detailInfo?.prcWaveDisp }}
                  </dd>
                </dl>
                <dl v-if="marketingCoupon(goodsInfo).length > 0">
                  <dt>상품 {{ marketingCoupon(goodsInfo)[0] }} 할인</dt>
                  <dd>
                    <em>-{{ marketingCoupon(goodsInfo)[1] }}</em
                    >원{{ goodsInfo?.detailInfo?.prcWaveDisp }}
                  </dd>
                </dl>

                <dl v-if="Number(goodsInfo?.promotionInfo?.sbDcAmt) > 0">
                  <dt>쇼핑북 할인</dt>
                  <dd>
                    <em>-{{ commaizeNumber(Number(goodsInfo?.promotionInfo?.sbDcAmt)) }}</em
                    >원
                  </dd>
                </dl>
                <dl v-if="Number(goodsInfo?.promotionInfo?.lspAmt) > 0">
                  <dt>일시불 할인</dt>
                  <dd>
                    <em>-{{ commaizeNumber(Number(goodsInfo?.promotionInfo?.lspAmt)) }}</em
                    >원
                  </dd>
                </dl>
              </div>
            </div>

            <button type="button" class="btn-close" @click="changeTooltips(TOOLTIP_TYPE.INFO1, false)">
              <span class="blind">안내 닫기</span>
            </button>
          </div>
        </div>
        <div v-if="isDc"></div>
        <div class="staff-price-wrap">
          <span class="dc-price-staff" v-if="Number(goodsInfo?.detailInfo?.staffDcPrice) > 0"
            >임직원 할인가 {{ commaizeNumber(setStaffSalePrice) }}원</span
          >
          <div
            class="tooltip-wrap"
            v-if="Number(goodsInfo?.detailInfo?.staffDcPrice) > 0 && Number(goodsInfo?.promotionInfo?.lspAmt) > 0"
          >
            <button type="button" class="btn-tooltip" @click="changeTooltips(TOOLTIP_TYPE.INFO2)">
              <span class="blind">안내</span>
            </button>
            <div class="tooltip-box" v-if="btnTooltipStaffDiscount">
              <div class="benefit-info">
                <dl class="dc-price-info">
                  <dt>총 가격 할인</dt>
                  <dd>
                    <em> -{{ staffDiscountAllPrice }} </em>원
                  </dd>
                </dl>
                <div class="benefit-list">
                  <dl>
                    <dt>임직원 할인</dt>
                    <dd>
                      <em> -{{ staffDiscountAfterLsp }}</em
                      >원
                    </dd>
                  </dl>
                  <dl>
                    <dt>일시불 할인</dt>
                    <dd>
                      <em> -{{ commaizeNumber(Number(goodsInfo?.promotionInfo?.lspAmt)) }}</em
                      >원
                    </dd>
                  </dl>
                </div>
              </div>
              <button type="button" class="btn-close" @click="changeTooltips(TOOLTIP_TYPE.INFO2, false)">
                <span class="blind">안내 닫기</span>
              </button>
            </div>
          </div>
        </div>
        <del class="origin-price" v-if="!isDc">{{ setSalePrice }}원{{ goodsInfo?.detailInfo?.prcWaveDisp }}</del>
      </div>

      <div class="notice-text" v-if="isConsultantGoods">상담 후 구매 가능한 상품입니다.</div>
      <span class="current-price" v-if="isRentalGoods && Number(monthlyRentalPrice) > 0"
        ><em>월</em> {{ monthlyRentalPrice }}<em>원</em></span
      >
      <span class="period-text" v-if="dutyUseDur">{{ dutyUseDur }}</span>
    </div>

    <div v-if="goodsReview > 0 || (orderQty >= 50 && !isConsultantGoods)" class="review-info">
      <button v-if="goodsReview > 0" type="button" class="btn-review" @click="_onChangeTab(TAB_TYPE.TAB3, '')">
        <div class="rating">
          <div class="star-wrap"><span>별점</span></div>
          <em>{{ ratingPoint }}</em>
        </div>
        <span class="number">({{ commaizeNumber(goodsReview) }})</span>
        <span class="recommend" v-if="Number(ratingPoint) >= 4.5"><i>고객추천</i></span>
      </button>
      <span class="buy-num" v-if="orderQty >= 50 && !isConsultantGoods">{{ commaizeNumber(orderQty) }} 구매</span>
    </div>

    <div class="label-wrap" v-if="goodsInfo?.labelInfo?.labelList">
      <span :class="item.classNm" v-for="(item, index) in goodsInfo?.labelInfo?.labelList" :key="index"
        ><i>{{ item.text }}</i></span
      >
    </div>
  </div>

  <div class="goods-warning-wrap" v-if="isConsultantGoods">
    <h4 class="title">
      <span>{{ goodsInfo?.detailInfo?.dispTypeCd === '45' ? '렌탈상품' : '상담상품' }}</span>
    </h4>
    <ul class="bullet-list">
      <li>NSmall이 상담 신청을 대행하는 상품으로, 상담 신청 비용이 발생하지 않습니다.</li>
      <li>협력사가 서비스를 직접 제공합니다.</li>
    </ul>
  </div>

  <HeadCopyCoupon v-if="!isConsultantGoods" :headCopy="goodsInfo?.headCopyInfo?.headCopyList" />
  <Benefit v-if="!isConsultantGoods && hasCardDiscount" />

  <details class="toggle-reserves-wrap" v-if="Number(goodsInfo?.promotionInfo?.acamtAmt) > 0">
    <summary>
      <strong class="title icon-reserves">
        {{ padValRto }}
        <span class="price">{{ commaizeNumber(Number(goodsInfo?.promotionInfo?.acamtAmt)) }}원</span> 적립
        {{ checkToday ? '가능' : '' }}</strong
      >
    </summary>
    <div class="toggle-box">
      <strong class="text">적립금 안내</strong>
      <div class="notice-box">
        <ul class="bullet-list">
          <li>적립금은 배송 완료 이후 제공됩니다.</li>
          <li>적립일로부터 30일 동안 사용할 수 있습니다.</li>
          <li>이 혜택은 예고 없이 변경될 수 있습니다.</li>
        </ul>
      </div>
    </div>
  </details>

  <div class="etc-benefit-wrap" v-if="iefrInstmMnmnt > 0 || freeInterestMonthByCard.length > 0">
    <dl class="icon-free" v-if="freeInterestMonthByCard.length === 0">
      <dt>무이자</dt>
      <dd>{{ iefrInstmMnmnt }}개월</dd>
    </dl>

    <div class="interest-free icon-free" v-else>
      <p>무이자 최대 {{ maxInterestFreeNumber }}개월</p>
      <div class="tooltip-wrap">
        <button type="button" class="btn-tooltip" @click="isShowInterestFree = true">
          <span class="blind">안내</span>
        </button>
      </div>
    </div>
    <ModalInterestFree
      v-model="isShowInterestFree"
      :iefrInstmMnmnt="iefrInstmMnmnt"
      :freeInterestMonthByCard="freeInterestMonthByCard"
    />
  </div>
</template>
<style lang="scss"></style>
